import { useState } from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, AlertTriangle } from "lucide-react";
import { api, type JobStatus } from "../api/client";
import JobProgressIndicator from "../components/JobProgressIndicator";

const DOC_TYPES = [
  { v: "protocol", l: "Clinical Trial Protocol" },
  { v: "investigator_brochure", l: "Investigator Brochure" },
  { v: "nice_submission", l: "NICE Submission" },
  { v: "ce_technical_file", l: "CE Technical File" },
  { v: "safety_narrative", l: "Safety Narrative" },
];
const FRAMEWORKS = ["MHRA", "EMA", "NICE", "ICH"];

export default function RegulatoryPage() {
  const [documentType, setDocumentType] = useState("protocol");
  const [framework, setFramework] = useState("MHRA");
  const [indication, setIndication] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [jobStatus, setJobStatus] = useState<JobStatus | "idle">("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [documentText, setDocumentText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (synopsis.trim().length < 50) {
      setError("Study synopsis must be at least 50 characters.");
      return;
    }
    if (indication.trim().length < 3) {
      setError("Indication is required.");
      return;
    }
    setJobStatus("pending");
    setElapsedMs(0);
    setError(null);
    setDocumentText(null);
    try {
      const res = await api.regulatoryDraft(
        {
          document_type: documentType,
          study_synopsis: synopsis,
          indication,
          framework,
        },
        (p) => {
          setJobStatus(p.status);
          setElapsedMs(p.elapsed_ms);
        }
      );
      setDocumentText(res.document);
      setJobStatus("completed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setJobStatus("failed");
    }
  };

  const isWorking = jobStatus === "pending" || jobStatus === "running";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardCheck className="text-nhs-purple" /> Regulatory Document Drafting
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          AI-drafted regulatory documents aligned with UK MHRA, EMA, NICE, and ICH frameworks.
        </p>
      </div>

      <div className="card">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label" htmlFor="doc-type">Document type</label>
            <select
              id="doc-type"
              className="input"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              disabled={isWorking}
            >
              {DOC_TYPES.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="framework">Framework</label>
            <select
              id="framework"
              className="input"
              value={framework}
              onChange={(e) => setFramework(e.target.value)}
              disabled={isWorking}
            >
              {FRAMEWORKS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="label" htmlFor="indication">Indication</label>
        <input
          id="indication"
          type="text"
          className="input mb-3"
          placeholder="e.g., Type 2 Diabetes Mellitus"
          value={indication}
          onChange={(e) => setIndication(e.target.value)}
          maxLength={500}
          disabled={isWorking}
        />
        <label className="label" htmlFor="synopsis">Study synopsis (min 50 characters)</label>
        <textarea
          id="synopsis"
          rows={6}
          className="input"
          value={synopsis}
          onChange={(e) => setSynopsis(e.target.value)}
          placeholder="Brief description of study design, population, endpoints..."
          maxLength={10000}
          disabled={isWorking}
        />
        <button onClick={submit} disabled={isWorking} className="btn-primary mt-3 w-full md:w-auto">
          {isWorking ? "Drafting..." : "Draft Document"}
        </button>
      </div>

      <JobProgressIndicator
        status={jobStatus}
        elapsedMs={elapsedMs}
        label="Drafting regulatory document..."
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded flex gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {documentText && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card">
          <h2 className="font-semibold mb-3">Drafted document</h2>
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-3 text-sm text-yellow-900">
            <strong>Regulatory disclaimer:</strong> This is an AI-generated draft. Must be
            reviewed by qualified regulatory affairs professionals before submission.
          </div>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800">
            {documentText}
          </div>
        </motion.div>
      )}
    </div>
  );
}
